import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Play, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Panel, StatusChip, ConfidenceBadge, PersonaStamp, LoadingState } from "@/components/app/primitives";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const STATUS_META = {
  queued: { label: "Queued", tone: "neutral" },
  running: { label: "Running", tone: "ai" },
  triad_review: { label: "Triad review", tone: "risk" },
  complete: { label: "Complete", tone: "validated" },
};

export default function AgentStudioPage() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [running, setRunning] = useState(false);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  const fetchAgents = useCallback(async () => {
    try {
      const res = await api.get("/agents");
      setData(res.data);
      if (res.data.all_complete && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRunning(false);
      }
    } catch (e) { /* transient */ }
  }, []);

  useEffect(() => {
    fetchAgents();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [fetchAgents]);

  const triggerRun = async () => {
    try {
      await api.post("/agents/run");
      setRunning(true);
      toast.success("Agent run started", { description: "12 agents processing evidence - lanes update live." });
      fetchAgents();
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(fetchAgents, 1500);
    } catch (e) {
      toast.error("Could not start run");
    }
  };

  if (!data) return <LoadingState message="Loading agent lanes..." />;
  const agents = data.agents || [];
  const completeCount = agents.filter((a) => a.status === "complete").length;

  return (
    <div data-testid="agents-page">
      <PageHeader
        kicker="Scene 3 - Agentic analysis"
        title="Agentic Analysis Studio"
        subtitle="Twelve specialized agents process evidence in visible lanes and report upward into the three senior personas. Every output carries evidence used, confidence, gaps, and recommended actions."
        right={
          <button onClick={triggerRun} disabled={running} data-testid="agent-run-button"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] active:scale-[0.98] px-4 py-2 text-xs font-semibold text-white transition-colors disabled:opacity-60">
            <Play size={13} /> {running ? "Run in progress..." : "Run assessment agents"}
          </button>
        }
      />

      <div className="flex items-center gap-3 mb-4 rounded-lg border border-[--divider] bg-[--shell-2] px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${running ? "bg-[--ai] lane-live" : "bg-[--green]"}`} />
          <span className="text-xs font-medium text-[--tx-1]" data-testid="agent-run-status">
            {running ? `Processing - ${completeCount}/12 lanes complete` : `All lanes complete - run #${data.run?.run_count || 1}`}
          </span>
        </div>
        <div className="h-4 w-px bg-[--divider]" />
        <span className="text-xs text-[--tx-3]">Hierarchy: evidence → specialized agents → senior persona challenge → actions, pilots, communications</span>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="agent-lane-board">
        {agents.map((agent) => {
          const meta = STATUS_META[agent.status] || STATUS_META.complete;
          return (
            <div key={agent.id} data-testid={`agent-lane-${agent.id}`}
              className={`rounded-xl border bg-[--panel-dark] p-3.5 transition-colors cursor-pointer hover:border-[--heat-3] ${agent.status === "running" ? "border-[--ai]" : "border-[--hairline]"}`}
              onClick={() => setSelected(agent)}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-[10px] text-[--tx-3] shrink-0">{agent.id}</span>
                  <h4 className="text-[13px] font-semibold text-[--tx-1] truncate">{agent.name}</h4>
                </div>
                <StatusChip tone={meta.tone} className={agent.status === "running" ? "lane-live" : ""} data-testid={`agent-status-${agent.id}`}>{meta.label}</StatusChip>
              </div>
              <Progress value={agent.progress} className="h-1 mb-2.5 bg-[--shell-3]" />
              <p className="text-[11px] text-[--tx-3] leading-snug mb-2.5 line-clamp-2">{agent.mandate}</p>
              {agent.status === "complete" || agent.status === "triad_review" ? (
                <div className="text-xs text-[--tx-2] leading-snug line-clamp-2 mb-2.5">{agent.findings?.[0]?.text}</div>
              ) : (
                <div className="text-xs text-[--tx-3] italic mb-2.5">{agent.status === "running" ? "Processing evidence..." : "Awaiting lane start"}</div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  {agent.persona_feeds.map((p) => {
                    const code = p.includes("Transformation") ? "TP" : p.includes("Data") ? "DS" : p.includes("Operator") ? "TO" : p.includes("All") ? "ALL" : "TO";
                    return code === "ALL" ? <StatusChip key={p} tone="crimson" className="!text-[9px]">Triad gate</StatusChip> : <PersonaStamp key={p} code={code} size="sm" />;
                  })}
                </div>
                <span className="text-[10px] text-[--tx-3] font-mono">{agent.evidence_used?.length || 0} evidence refs</span>
              </div>
            </div>
          );
        })}
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-[--shell-2] border-[--hairline] w-full sm:max-w-lg overflow-y-auto thin-scroll" data-testid="agent-detail-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="text-[--tx-1] font-display flex items-center gap-2">
                  <span className="font-mono text-xs text-[--tx-3]">{selected.id}</span> {selected.name}
                </SheetTitle>
                <SheetDescription className="text-[--tx-3] text-xs">{selected.mandate}</SheetDescription>
              </SheetHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1.5">Findings</div>
                  <div className="space-y-2">
                    {selected.findings?.map((f, i) => (
                      <div key={i} className="rounded-lg bg-[--panel-dark] border border-[--divider] p-3">
                        <p className="text-xs text-[--tx-2] leading-relaxed mb-1.5">{f.text}</p>
                        <div className="flex items-center gap-2">
                          <ConfidenceBadge level={f.confidence} />
                          {f.finding_ref && (
                            <button onClick={() => navigate("/triad", { state: { findingId: f.finding_ref } })}
                              className="inline-flex items-center gap-1 text-[11px] text-[--ai] hover:text-[#35B7B1] font-medium transition-colors"
                              data-testid={`agent-finding-triad-link-${f.finding_ref}`}>
                              {f.finding_ref} in Triad Room <ArrowUpRight size={11} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><div className="text-[--tx-3] mb-1">Evidence used</div><div className="flex flex-wrap gap-1">{selected.evidence_used?.map((e) => <span key={e} className="font-mono text-[10px] rounded bg-[--shell-3] border border-[--divider] px-1.5 py-0.5 text-[--tx-2]">{e}</span>)}</div></div>
                  <div><div className="text-[--tx-3] mb-1">Confidence</div><p className="text-[--tx-2] leading-snug">{selected.confidence}</p></div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1">Follow-up questions</div>
                  <ul className="space-y-1">{selected.follow_up_questions?.map((q, i) => <li key={i} className="text-xs text-[--tx-2] leading-relaxed pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:w-1 before:h-1 before:rounded-full before:bg-[--ai]">{q}</li>)}</ul>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1">Recommended actions</div>
                  <ul className="space-y-1">{selected.recommended_actions?.map((a, i) => <li key={i} className="text-xs text-[--tx-2] leading-relaxed pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:w-1 before:h-1 before:rounded-full before:bg-[--green]">{a}</li>)}</ul>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><div className="text-[--tx-3] mb-0.5">Owner needed</div><div className="text-[--tx-1] font-medium">{selected.owner_needed}</div></div>
                  <div><div className="text-[--tx-3] mb-0.5">Timing</div><div className="text-[--tx-1] font-medium">{selected.timing}</div></div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-0.5">Measurement method</div>
                  <p className="text-xs text-[--tx-2] leading-relaxed">{selected.measurement}</p>
                </div>
                <div className="rounded-lg bg-[--panel-dark] border border-[--divider] p-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1.5">Reports upward to</div>
                  <div className="flex items-center gap-2">{selected.persona_feeds.map((p) => <StatusChip key={p} tone="crimson">{p}</StatusChip>)}</div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
